import {
    ColumnType,
    DataGridTooltipedFormatter,
    DropDown,
    getFilterElementsDefault,
    getMaskedPhone,
    getMaskFromName,
    PhoneNumber,
    selectOptionsToDropDown,
    sharpDateFormatter,
    useErrorState,
    useWorkplaceState,
} from '@finbeat/react-toolkit';
import { getSelectList } from 'axios/dictionaryApi';
import {
    getPreorderGeneralInfo,
    getPreorders,
    getPreordersExcel,
    getPreorderStatuses,
} from 'axios/preorderApi';
import { getTradeOrgsList } from 'axios/tradeOrgsApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import DataTimeText from 'components/Common/DataTime';
import Money from 'components/Common/Money';
import { subDays } from 'date-fns';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import {
    PreorderDetailsGeneralInfo,
    PreorderFilterType,
    PreorderTableItem,
} from 'models/api/preorder';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useNotifyContext } from 'providers/NotifyProvider';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { getEndOfTheDay, getStartOfTheDay } from 'utils/dataTime';
import PreorderFilter from './PreorderFilter';
import PreorderChangeModal from './PreorderModal/PreorderChangeModal';
import { dictValueFormatter } from 'utils/columnFormatters';

const Preorder = () => {
    const { t, i18n } = useTranslation();
    const { hasRole } = useAuth();
    const { handleRequestError } = useErrorState();
    const { addSubscription, removeSubscription } = useNotifyContext();
    const { dateTimeFormat } = useGeneralSettingsContext();
    const api = useApi();
    const queryClient = useQueryClient();

    const [isModalVisible, setModalVisible] = useState(false);
    const [orderStatuses, setOrderStatuses] = useState<DropDown[]>([]);
    const [tradeOrgs, setTradeOrgs] = useState<DropDown[]>([]);
    const [sources, setSources] = useState<DropDown[]>([]);

    const getOrderStatusesQuery = useQuery(
        ['preorderStatuses', i18n.language],
        () => getPreorderStatuses(api),
        {
            onSuccess: (data) => {
                setOrderStatuses(
                    data.filter((s) => ['preorder', 'new'].indexOf(s.id?.toString() ?? '') >= 0)
                );
            },
            onError: (err) => handleRequestError(err),
        }
    );

    const getTradeOrgsQuery = useQuery('TradeOrgs', () => getTradeOrgsList(api), {
        onSuccess: (data) => setTradeOrgs(selectOptionsToDropDown(data ?? [])),
        onError: (err) => handleRequestError(err),
    });

    const getDictsQuery = useQuery(
        ['preorderDicts', i18n.language],
        () => getSelectList(api, ['TOIntegrationType']),
        {
            onSuccess: (data) => {
                if (data?.TOIntegrationType) setSources(data.TOIntegrationType);
            },
            onError: (err) => {
                handleRequestError(err);
            },
        }
    );
    const { data: brandCodeResponse, isFetched: isFetchingBrandCodes } = useQuery(
        'brandCodes',
        () => getSelectList(api, ['BrandCode']),
        {
            onError: (err) => handleRequestError(err),
            enabled: hasRole('visibleBrandCodesFilter'),
        }
    );

    const brandCodes = useMemo<DropDown[]>(() => {
        return brandCodeResponse?.['BrandCode'] ?? [];
    }, [brandCodeResponse]);

    const queryProductTypes = useQuery(
        ['productTypes', i18n.language],
        () => getSelectList(api, ['ProductTypes']),
        {
            onError: (e) => handleRequestError(e),
        }
    );
    const productTypes = useMemo<DropDown[]>(() => {
        return queryProductTypes.data?.['ProductTypes'] ?? [];
    }, [queryProductTypes.data]);

    useEffect(() => {
        queryClient.invalidateQueries([['preorder']]);
    }, [i18n.language]);

    useEffect(() => {
        addSubscription({
            key: 'onToOrderStatusChange',
            type: 'ToOrderStatusChange',
            callback: () => {
                queryClient.invalidateQueries([['preorder']]);
            },
        });

        return () => {
            removeSubscription('onToOrderStatusChange');
        };
    }, []);

    const initColumns = useMemo<ColumnType<PreorderTableItem, PreorderTableItem[]>[]>(
        () => [
            {
                key: 'fio',
                name: <Trans i18nKey={'BackOfficeClient'} />,
                cellClass: 'normalLineHeight',
                formatter: ({ row: { fio, phoneNumber } }) => {
                    const phoneFormatted = getMaskedPhone(phoneNumber, getMaskFromName('Default'));

                    return (
                        <DataGridTooltipedFormatter
                            tooltipText={`${fio} ${phoneFormatted}`}
                            overflownElementClassName="text-ellipsis"
                            className="pt-2"
                        >
                            {fio}
                            <div className="small font-weight-light text-ellipsis">
                                <PhoneNumber value={phoneNumber} />
                            </div>
                        </DataGridTooltipedFormatter>
                    );
                },
            },
            {
                key: 'id',
                columnsForSorting: [
                    {
                        key: 'visibleId',
                        name: 'BackOfficeId',
                    },
                    {
                        key: 'dt',
                        name: 'BackOfficeCreateDate',
                    },
                ],
                name: <Trans i18nKey={'BackOfficeOrderId'} />,
                cellClass: 'normalLineHeight',
                formatter: ({ row: { id, dt, orderId } }) => (
                    <DataGridTooltipedFormatter
                        tooltipText={`${orderId ?? id} ${sharpDateFormatter(dt, dateTimeFormat)}`}
                        overflownElementClassName="text-ellipsis"
                        className="pt-2"
                    >
                        #{orderId ?? id}
                        <div className="small font-weight-light text-ellipsis">
                            <DataTimeText value={dt} />
                        </div>
                    </DataGridTooltipedFormatter>
                ),
            },
            {
                key: 'orderSum',
                name: <Trans i18nKey={'BackOfficeSumTotal'} />,
                cellClass: 'normalLineHeight',
                formatter({ row }) {
                    const { orderSum } = row;
                    return <Money value={orderSum} />;
                },
            },
            {
                key: 'statusName',
                name: <Trans i18nKey={'BackOfficeOrderStatus'} />,
                cellClass: 'normalLineHeight',
            },
            {
                key: 'tradeOrg',
                columnsForSorting: [
                    {
                        key: 'tradeOrgName',
                        name: 'BackOfficeTradeOrg',
                    },
                    {
                        key: 'outletName',
                        name: 'BackOfficeOutlet',
                    },
                ],
                name: <Trans i18nKey={'BackOfficeMerchant'} />,
                cellClass: 'normalLineHeight',
                formatter: ({ row: { tradeOrgName, outletName } }) => (
                    <DataGridTooltipedFormatter
                        overflownElementClassName="text-ellipsis"
                        className="pt-2"
                    >
                        {`${t('BackOfficeTradeOrg')}: ${tradeOrgName}`}
                        <div className="font-weight-light text-ellipsis">
                            {`${t('BackOfficeOutlet')}: ${outletName}`}
                        </div>
                    </DataGridTooltipedFormatter>
                ),
            },
            {
                key: 'source',
                name: <Trans i18nKey={'BackOfficeSource'} />,
                cellClass: 'normalLineHeight',
            },
            {
                key: 'channel',
                name: <Trans i18nKey={'BackOfficeChannel'} />,
                cellClass: 'normalLineHeight',
            },
            {
                key: 'productTypeId',
                name: <Trans i18nKey={'BackOfficeProductType'} />,
                cellClass: 'normalLineHeight',
                formatter: dictValueFormatter('productTypeId', productTypes),
            },
        ],
        [dateTimeFormat, t, productTypes]
    );

    return (
        <BOWorkplace<PreorderTableItem, PreorderDetailsGeneralInfo, PreorderFilterType>
            pageTitle="BackOfficePreorders"
            defaultPageSize={10}
            filterParams={{
                sd: getStartOfTheDay(subDays(new Date(), 7)),
                ed: getEndOfTheDay(),
                orderInfo: '',
                statusCode: '',
                tradeOrgId: null,
                outletId: null,
                brandCode: '',
            }}
            initColumns={initColumns}
            storagePath="preorder/columns"
            lineHeight={60}
            showExcelBtn={hasRole('preorderExcel')}
            fetchExcelQueryFn={(filter) => getPreordersExcel(api, filter)}
            fetchQueryFn={(filter) => getPreorders(api, filter)}
            getFilterElements={getFilterElementsDefault([
                {
                    id: 'sd',
                    formatter: (val) =>
                        `${t('BackOfficeDtFrom')}: ${sharpDateFormatter(val, dateTimeFormat)}`,
                },
                {
                    id: 'ed',
                    formatter: (val) =>
                        `${t('BackOfficeTo')}: ${sharpDateFormatter(val, dateTimeFormat)}`,
                },
                { name: t('BackOfficeOrderInfo'), id: 'orderInfo' },
                {
                    id: 'statusCode',
                    name: t('BackOfficeOrderStatus'),
                    formatter: (val) => orderStatuses.find((x) => x.id === val)?.name ?? val,
                },
                {
                    id: 'tradeOrgId',
                    name: t('BackOfficeTradeOrg'),
                    formatter: (val) =>
                        tradeOrgs?.find((t) => t.id === val)?.name ?? val.toString(),
                },
                {
                    id: 'source',
                    name: t('BackOfficeSource'),
                    formatter: (val) => sources?.find((t) => t.id === val)?.name ?? val.toString(),
                },
                {
                    id: 'productTypeId',
                    name: t('BackOfficeProductType'),
                    formatter: (val) => productTypes?.find((t) => t.id === val)?.name ?? val,
                },
                {
                    id: 'brandCode',
                    name: t('BackOfficeBrandCode'),
                    formatter: (val) =>
                        brandCodes?.find((o) => o.id === val)?.name ?? val.toString(),
                },
            ])}
            showModal={() => setModalVisible(true)}
            filter={
                <PreorderFilter
                    orderStatuses={orderStatuses}
                    tradeOrgs={tradeOrgs}
                    sources={sources}
                    brandCodes={brandCodes ?? []}
                    productTypes={productTypes ?? []}
                    isFetchingBrandCodes={isFetchingBrandCodes}
                    getOrderStatusesQuery={getOrderStatusesQuery}
                    getTraderOrgsQuery={getTradeOrgsQuery}
                    getDictsQuery={getDictsQuery}
                />
            }
            modal={{
                component: isModalVisible ? (
                    <PreorderChangeModal
                        isOpen={isModalVisible}
                        toggle={() => setModalVisible(false)}
                    />
                ) : (
                    <></>
                ),
                fetchCurrentItemQueryFn: (id) => getPreorderGeneralInfo(api, id),
            }}
            queryKey={['preorder']}
            queryClient={queryClient}
        />
    );
};

export default Preorder;
const usePreorderWorkplaceState = () =>
    useWorkplaceState<PreorderTableItem, PreorderDetailsGeneralInfo, PreorderFilterType>();

export { usePreorderWorkplaceState };
